/**
 * InsufficientCreditsHandler — global provider for 402 insufficient_credits errors.
 *
 * A15-N7. Listens for 'aif:insufficient-credits' window events
 * (dispatched by apiClient on 402 with code=insufficient_credits),
 * then shows TopUpModal with contextual reason text.
 *
 * Usage: wrap App content with <InsufficientCreditsProvider>.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import TopUpModal from './TopUpModal';

const InsufficientCreditsContext = createContext({ show: () => {} });

export function useInsufficientCreditsHandler() {
  return useContext(InsufficientCreditsContext);
}

export function InsufficientCreditsProvider({ children }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState(null);

  const show = useCallback((detail) => {
    const balance = detail?.balance ?? 0;
    const required = detail?.required ?? 0;
    const message = detail?.message || 'Sie benötigen mehr Credits, um diese Anfrage auszuführen.';

    if (required > 0) {
      setReason(`${message} (${balance.toLocaleString('de-DE')} vorhanden, ${required.toLocaleString('de-DE')} benötigt)`);
    } else {
      setReason(message);
    }
    setModalOpen(true);
  }, []);

  // Listen for global window events dispatched by apiClient
  useEffect(() => {
    function handler(event) {
      show(event.detail);
    }
    window.addEventListener('aif:insufficient-credits', handler);
    return () => window.removeEventListener('aif:insufficient-credits', handler);
  }, [show]);

  return (
    <InsufficientCreditsContext.Provider value={{ show }}>
      {children}
      {modalOpen && (
        <TopUpModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          reason={reason}
        />
      )}
    </InsufficientCreditsContext.Provider>
  );
}
